import React, { useState, useEffect } from 'react';
import {
  Spinner, FlexBox, Typography, Link, Visible,
} from '@vp/swan';
import { defineMessages, useTranslation } from '@vp/i18n-helper';
import { getCurrentLocale } from '../utils/locale';

import { authLogin, getAuthIdentity, isSignedIn } from '../utils/authUtils';
import { getQueryParam } from '../utils/utils';
import { generateAlbelliEditorUrl, getDoc } from '../infraClients/albelliIntegrationLayerClient';
import '../styles.css';
import { getCoreProductId, getProductName, getMpvId } from '../infraClients/msxClient';
import { logError } from '../newRelicWrapper';

const messages = defineMessages({
  unableToGetEditorUrlMsg: {
    id: 'albelli.index.errorMsg.unableToGetEditorUrl',
    defaultMessage: 'Unable to initialize editor url, refresh the page.',
  },
  loading: {
    id: 'albelli.index.loading',
    defaultMessage: 'Loading',
  },
  userMessageFirst: {
    id: 'albelli.index.userMessage.first',
    defaultMessage: 'Loading your editor now...',
  },
  userMessageSecond: {
    id: 'albelli.index.userMessage.second',
    defaultMessage: 'As you design, we recommend saving early and often…',
  },
  continueInOffline: {
    id: 'albelli.index.offlineMsg.continueInOffline',
    defaultMessage: 'Please continue editing your Photo Book in the offline editor',
  },
  unableToGetDocMsg: {
    id: 'albelli.index.errorMsg.unableToGetDoc',
    defaultMessage: 'Unable to load document.',
  },
  homePage: {
    id: 'albelli.index.offlineMsg.homePageLink',
    defaultMessage: 'Home',
    description: {
      note: 'Link for user to click to go back to home page',
    },
  },
});

function getInfoFromDocument(currentDoc) {
  let numberOfImages = 0;
  if (currentDoc.images !== undefined && currentDoc.images !== null) {
    numberOfImages = currentDoc.images.length;
  }

  const editorType = currentDoc.attributes.Editor;
  return { numberOfImages, editorType };
}

export function IndexView() {
  const [errorMsg, setErrorMsg] = useState('');
  const [showOfflineMsg, setShowOfflineMsg] = useState(false);
  const [showLoadingMsg, setShowLoadingMsg] = useState(false);
  const { t } = useTranslation();

  function showError(e, errorMessage) {
    logError(e);
    setErrorMsg(errorMessage);
  }

  async function init() {
    let errorMessage;
    let editorUrl = null;
    const locale = getCurrentLocale();
    const owner = getQueryParam('owner');
    let vpProductKey = getQueryParam('key');
    const careAgent = getQueryParam('careAgent');
    const editorEnvironmentOverride = getQueryParam('editorEnvironmentOverride');
    const docId = getQueryParam('docId');
    const selectedOptions = (getQueryParam('selectedOptions'));
    const predesignedProductId = getQueryParam('predesignedProductId');
    let vpProductName = getQueryParam('vpProductName');

    const signedInAlready = await isSignedIn();
    if (!signedInAlready && careAgent) {
      await authLogin();
      return;
    }

    await getAuthIdentity();

    let currentDoc;
    let mpvId;
    if (docId) {
      try {
        currentDoc = await getDoc(docId, owner);
        mpvId = currentDoc.mpv;
      } catch (e) {
        errorMessage = t(messages.unableToGetDocMsg.id);
        showError(e, errorMessage);
        return;
      }
    } else if (vpProductKey) {
      mpvId = await getMpvId(vpProductKey, locale);
    }

    if (!vpProductKey && mpvId) {
      vpProductKey = await getCoreProductId(mpvId, locale);
    }
    if (!vpProductName && vpProductKey) {
      vpProductName = await getProductName(vpProductKey, locale);
    }

    if (!vpProductKey) {
      logError('Did not receive or could not retrieve a product key');
    }

    window.tracking.page({
      pageSection: 'Studio', // the section of the page
      pageStage: 'Design', // the stage of the page
      pageName: `${mpvId}:Studio`, // detailed name of the page where the event has been sent;
      language: locale.substring(0, 2),
      locale: locale.substring(3, 5).toUpperCase(),
    });
    window.tracking.track('Product Viewed', {
      label: 'Studio page view', // the event label name
      pageSection: 'Studio', // the section of the page
      pageStage: 'Design', // the stage of the page
      pageName: `${mpvId}:Studio`, // detailed name of the page where the event has been sent
      product_id: mpvId, // MPVID
      name: vpProductName, // Detailed Product Name / MPV
      core_product_id: vpProductKey,
      language: locale.substring(0, 2),
      locale: locale.substring(3, 5).toUpperCase(),
    });

    if (currentDoc) {
      const { editorType } = getInfoFromDocument(currentDoc);
      // For offline editor
      if (currentDoc.attributes && editorType
        && (editorType === 'MacEditor' || editorType === 'WindowsEditor')) {
        setShowOfflineMsg(true);
        return;
      }
    }

    setShowLoadingMsg(true);

    try {
      editorUrl = await generateAlbelliEditorUrl({
        locale,
        docId,
        predesignedProductId,
        selectedOptions,
        vpProductKey,
        owner,
        careAgent,
        vpProductName,
        editorEnvironmentOverride,
      });
    } catch (e) {
      errorMessage = t(messages.unableToGetEditorUrlMsg.id);
      showError(e, errorMessage);
      return;
    }

    setTimeout(() => { window.location.replace(editorUrl); }, 3000);
  }

  useEffect(() => {
    init();
  }, []);

  if (showOfflineMsg) {
    const homePage = window.location.origin;
    return (
      <FlexBox className="offline-message-view"
        justifyContent="center"
        alignItems="center"
        flexDirection="column">
        <h1 style={{ textAlign: 'center' }}> {t(messages.continueInOffline.id)} </h1>
        <Link href={homePage}>{t(messages.homePage.id)}</Link>
      </FlexBox>
    );
  }

  return (
    <div className="index-view">
      <Visible md lg xl>
        <FlexBox justifyContent="center">
          <Spinner accessibleText={t(messages.loading.id)} size="super" />
        </FlexBox>
      </Visible>
      <Visible sm xs>
        <FlexBox justifyContent="center">
          <Spinner accessibleText={t(messages.loading.id)} />
        </FlexBox>
      </Visible>

      {showLoadingMsg
        && (<>
          <h1 style={{ textAlign: 'center' }}> {t(messages.userMessageFirst.id)} </h1>
          <h2 style={{ textAlign: 'center' }}> {t(messages.userMessageSecond.id)} </h2>
          <br />
        </>)
      }
      {errorMsg && (<Typography textColor="error" textAlign='center'>{errorMsg}</Typography>)
      }

    </div>
  );
}

export default IndexView;
